/**
 * Order Item Component
 */
import React, { memo } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';
import ItemWithReview from './ItemWithReview';
import { STORE_CUSTOMIZATION } from '@/utils/constants';
import { getStoreType } from '@/utils/getStoreType';
import classNames from 'classnames';

export function getOrderMode(order) {
  return order?.order_mode;
}

const OrderItem = ({
  order,
  isComplete,
  isFailedPaymentOrder,
  onBuyAgainClick,
  onCardClick,
}) => {
  const { theme, services } = useSelector((state) => state.storeReducer.store);
  const isServiceCustomizationEnabled =
    getOrderMode(order) !== STORE_CUSTOMIZATION.ORDER_BASED &&
    getOrderMode(order) !== STORE_CUSTOMIZATION.B2B;
  const isB2bOrder = getOrderMode(order) === STORE_CUSTOMIZATION.B2B;

  const getDisplayTextFromStatusText = (statusText, amount) => {
    switch (statusText) {
      case 'new':
        if (getStoreType() === STORE_CUSTOMIZATION.B2B) return 'Request Raised';
        else if (isServiceCustomizationEnabled) return 'Booking created';
        else return 'Order Placed';
      case 'billSent':
        return 'Pay Now';
      case 'rejected':
        return isServiceCustomizationEnabled ? 'Booking Rejected' : 'Order Rejected';
      case 'sendBill':
      case 'outForDelivery':
      case 'markReady':
        return isB2bOrder
          ? 'Request is being Reviewed'
          : isServiceCustomizationEnabled
            ? 'Booking is being Reviewed'
            : 'Order Seen';
      case 'prepaidPickupReady':
        return isServiceCustomizationEnabled ? 'Service Available' : 'Ready For Pickup';
      case 'prepaidDeliveryReady':
        return isServiceCustomizationEnabled ? 'Service Available' : 'Out For Delivery';
      case 'completedOnline':
      case 'completedCash':
      case 'cashMarked':
      case 'paidOnline':
        return `₹ ${amount || 0}`;
      default:
        return '';
    }
  };

  const getOrderTypeLabel = () => {
    switch (getStoreType()) {
      case STORE_CUSTOMIZATION.ORDER_BASED:
        return 'Order ';
      case STORE_CUSTOMIZATION.B2B:
        return 'Request #';
      default:
        return 'Booking ';
    }
  };

  const showWarning = order.display_status === 'rejected' || isFailedPaymentOrder;

  function renderStatus() {
    if (order?.refund_info) {
      return (
        <div className="refunded">
          <div className="tw-flex tw-flex-col tw-items-end">
            <div className="tw-text-13 tw-font-medium">Refunded</div>
            <div className="ref-id tw-mt-0.5 tw-text-12 tw-font-medium sm:tw-hidden">
              ID:{order.refund_info?.refund_id}
            </div>
          </div>
          <div className="tw-mt-16 tw-flex tw-flex-col tw-items-end sm:tw-mt-4">
            <div className="tw-text-13 tw-font-medium sm:tw-text-[#999]">
              Bank Reference ID
            </div>
            <div className="ref-id tw-mt-0.5 tw-text-12 tw-font-medium">
              {order.refund_info?.bank_reference_number}
            </div>
          </div>
        </div>
      );
    }
    return (
      <>
        {getDisplayTextFromStatusText(order.display_status, order.pay_amount)}
        {order.prepaid_flag === 1 && (
          <div className="f7 fw3 black-60 w-100 flex justify-end items-center pt1">
            Prepaid Online
          </div>
        )}
      </>
    );
  }

  return (
    <article
      className={classNames(
        'ds-order-item',
        isFailedPaymentOrder ? '!tw-cursor-default' : ''
      )}
      onClick={onCardClick}
      key={`order-items-${order?.order_id}`}
    >
      <div className="ds-order-item__head">
        <div className="top-wrap">
          <span className="oid">
            {getOrderTypeLabel()}
            {order.order_id}
          </span>
          <span
            className={`${showWarning ? 'warning' : ''} tl status`}
            style={
              !showWarning
                ? {
                    color: theme?.colors?.primary_color,
                  }
                : {}
            }
          >
            {isFailedPaymentOrder ? 'Payment Failed' : renderStatus()}
          </span>
        </div>
        <div className="time-wrap flexRowAlign">
          <span>{moment(order.created_at).format('hh:mm a')}</span>
          <span className="flexRowAlign">
            {order.display_status === 'completedOnline' ||
            order.display_status === 'paidOnline' ||
            order.display_status === 'prepaidDeliveryCompleted' ||
            order.display_status === 'prepaidPickupCompleted'
              ? 'Online Payment'
              : ''}
            {order.display_status === 'completedCash' ||
            order.display_status === 'cashMarked'
              ? 'Cash Payment'
              : ''}
          </span>
        </div>
      </div>
      <div className="ds-order-item__body">
        {order.items &&
          order.items.map((item, index) => (
            <ItemWithReview
              order={order}
              item={item}
              isComplete={isComplete}
              key={index}
            />
          ))}
      </div>
      {services?.store_flag !== 0 && isComplete && (
        <div
          className="ds-order-item__footer"
          style={{
            justifyContent: 'center',
          }}
        >
          <span
            className="reorder-btn-wrap"
            onClick={onBuyAgainClick}
            style={{
              color: theme?.colors?.primary_color,
              borderColor: theme?.colors?.primary_color,
            }}
          >
            Buy Again
          </span>
        </div>
      )}
    </article>
  );
};

export default memo(OrderItem);
